import styles from './VpnUsers.module.css'
import { gql } from "@apollo/client"
import { useApolloClient } from "@apollo/client/react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Col, Collapse, Container, Row } from "react-bootstrap"
import User, { VpnUserType } from '../users/User'
import Notification, { AlertItem } from "../../../../../components/notification/Notification"
import SearchContainer from './search-container/SearchContainer'
import { useModifyPage, useModifyRecordsPerPage, usePaginationData, useSetSearchUrlParams } from '../../../../../hooks/usePagination'
import { useSearchParams } from 'react-router-dom'
import Pagination from '../../../../../components/pagination/Pagination'
import ErrorMessage from '../../../../../components/error-message/ErrorMessage'
import CreateUser from '../users/create-user/CreateUser'
import LoadingSkeleton from '../../../../../components/loading-skeleton/LoadingSkeleton'

const VPN_USERS_QUERY = gql`
    query VpnUsers($searchParameters: VpnSearchParameters, $orderBy: VPNUserSortField, $direction: SortDirection, $pagination: PaginationInput) {
        vpnUsers(searchParameters: $searchParameters, orderBy: $orderBy, direction: $direction, pagination: $pagination) {
            maxPages
            currentPage
            recordsPerPage
            vpnUsers {
                id
                username
                rtrComment
                totpSecret
                totpSecretDataUrl
                enabled
            }
            success
            errorMessage
        }
    }
`

interface vpnUsersQueryResult {
    vpnUsers: {
        maxPages: number
        currentPage: number
        recordsPerPage: number
        success: boolean,
        errorMessage: string,
        vpnUsers: VpnUserType[]
    }
}

export interface modifyUserData {
    enabled?: boolean
    generateTotpSeed?: boolean
    generateUUID?: boolean
}

const MODIFY_USER_QUERY = gql`
    mutation UpdateVpnUser($updateVpnUserInput: UpdateVpnUserInput!) {
        updateVpnUser(updateVpnUserInput: $updateVpnUserInput) {
            id
            username
            totpSecretDataUrl
            totpSecret
            rtrComment
            enabled
        }
    }
`

interface modifyUserResult {
    updateVpnUser: VpnUserType
}

export type VpnUserSearchParams = {
    id: number | null
    username: string | null
    rtrComment: string | null //UUID
    enabled: boolean | null
}

enum USER_SORT_DIRECTION {
    ASC = "asc",
    DESC = "desc"
}

enum USER_SORT_FIELD {
    ID = "id",
    USERNAME = "username",
    ACTIVE = "active"
}

export type SortSearchParamsType = {
    field: USER_SORT_FIELD | null
    direction: USER_SORT_DIRECTION | null
}

export default function VpnUsers() {
    const client = useApolloClient()
    const [ searchParams, setSearchParams ] = useSearchParams()
    const [ maxPages, setMaxPages ] = useState(-1)
    const [ vpnUsers, setVpnUsers ] = useState<VpnUserType[]>([])
    const [ alerts, setAlerts ] = useState<AlertItem[]>([])
    const [ loading, setLoading ] = useState(true)
    const pagination = usePaginationData(searchParams, maxPages)
    const modifyPage = useModifyPage(setSearchParams, pagination)
    const modifyRecordsPerPage = useModifyRecordsPerPage(setSearchParams)
    const [ showError, setShowError ] = useState(false)
    const [ errorMessage, setErrorMessage ] = useState("")
    const [ showCreateUserModal, setShowCreateUserModal ] = useState(false)
    const setSearchUrlParams = useSetSearchUrlParams(setSearchParams)

    const searchParameters = useMemo<VpnUserSearchParams>(() => {
        let id: number | null = searchParams.get('id') !== null ? Number(searchParams.get('id')) : null
        if(isNaN(id as number)) {
            id = null
        }
        
        let username: string | null = searchParams.get('username')
        let rtrComment: string | null = searchParams.get('rtrcomment')

        const enabledParam = searchParams.get('enabled')

        let enabled: boolean | null = null

        if (enabledParam !== null) {
            enabled = enabledParam === 'true' // csak 'true' lesz true, minden más false
        }

        const ret: VpnUserSearchParams = {
            id,
            username,
            rtrComment,
            enabled
        }
        return ret
    }, [searchParams])

     const sortBy = useMemo<SortSearchParamsType>(() => {
        let orderBy = searchParams.get('orderby') as (USER_SORT_FIELD | null)
        let direction = searchParams.get('direction') as (USER_SORT_DIRECTION | null)

        if(orderBy !== null) {
            orderBy = orderBy.toLowerCase() as USER_SORT_FIELD
            if(!Object.values(USER_SORT_FIELD).includes(orderBy)) {
                orderBy = null
            }
        }

        if(orderBy === null) {
            direction = null
        }
        else {
            if(direction !== null) {
                direction = direction.toLowerCase() as USER_SORT_DIRECTION
                if(!Object.values(USER_SORT_DIRECTION).includes(direction)) {
                    direction = null
                }
            }
        }

        return {
            field: orderBy,
            direction
        }
    }, [searchParams])

    const getVpnUsers = useCallback(async () => {
        setLoading(true)

        const data = await client.query<vpnUsersQueryResult>({
            query: VPN_USERS_QUERY,
            variables: {
                searchParameters,
                orderBy: sortBy.field ? sortBy.field.toUpperCase() : undefined,
                direction: sortBy.direction ? sortBy.direction.toUpperCase() : undefined,
                pagination
            },
            fetchPolicy: 'no-cache'
        })

        const { maxPages, recordsPerPage, currentPage, vpnUsers, success, errorMessage } = data.data!.vpnUsers
        setMaxPages(maxPages)

        if(pagination.recordsPerPage !== recordsPerPage) {
            modifyRecordsPerPage(recordsPerPage)
        }

        if(pagination.currentPage !== currentPage) {
            modifyPage(currentPage)
        }

        setShowError(!success || !vpnUsers || vpnUsers.length === 0)
        setErrorMessage(errorMessage)

        if(vpnUsers) {
            setVpnUsers(vpnUsers)
        }
        setLoading(false)
    }, [ client, sortBy, pagination, modifyPage, modifyRecordsPerPage, searchParameters ])

    useEffect(() => {
        getVpnUsers()
    }, [ getVpnUsers ])

    const toggleSort = (field: USER_SORT_FIELD) => {
        if(loading) {
            return
        }

        let newDirection = USER_SORT_DIRECTION.ASC
        if(sortBy.field === field) {
            newDirection = sortBy.direction === USER_SORT_DIRECTION.ASC ? USER_SORT_DIRECTION.DESC : USER_SORT_DIRECTION.ASC
        }

        setSearchUrlParams({
            orderby: field,
            direction: newDirection
        })

        setLoading(true)
    }

    const getArrowSrc = (field: USER_SORT_FIELD) => {
        if (sortBy.field !== field) {
            return "/updown-arrow.png"
        }
        return sortBy.direction === "asc" ? "/uparrow.png" : "/downarrow.png"
    }

    const modifyUser = async (userId: number, data: modifyUserData) => {
        try {
            const response = await client.mutate<modifyUserResult>({
                mutation: MODIFY_USER_QUERY,
                variables: {
                    updateVpnUserInput: {
                        ...data,
                        id: userId
                    }
                },
                fetchPolicy: 'no-cache'
            })

            const vpnUser = response.data?.updateVpnUser
            if(vpnUser) {
                setVpnUsers(prev => prev.map(user => user.id !== vpnUser.id ? user : vpnUser))

                const alert: AlertItem = {
                    id: new Date().getTime(),
                    type: "success",
                    title: `Sikeres módosítás`,
                    message: `Sikeres módosítás`
                }
                setAlerts(prev => [...prev, alert])
                
                return
            }
        }
        catch(e) {
            console.log(e)
        }

        const alert: AlertItem = {
            id: new Date().getTime(),
            type: "danger",
            title: `Hiba a módosítás közben`,
            message: `Hiba a módosítás közben`
        }
        setAlerts(prev => [...prev, alert])
    }

    return (
        <>
            <SearchContainer
                plusButtonOnClick={() => setShowCreateUserModal(true)}
                searchParameters={ searchParameters }
                setSearchParams={ setSearchParams }
                pagination= {
                    <Pagination
                        maxPages={ maxPages }
                        currentPage={ pagination.currentPage }
                        recordsPerPageProps="Felhasználó / Oldal"
                        recordsPerPage={ pagination.recordsPerPage }
                        modifyPage={page => {
                            modifyPage(page)
                            setLoading(true)
                        }}
                        modifyRecordsPerPage={rpp => {
                            modifyRecordsPerPage(rpp)
                            setLoading(true)
                        }}
                    />
                }
            />

            <Container className={ styles.userContainer }>
                <Row className={styles.containerHead}>
                    <Col sm="2">ID
                        <button onClick={() => toggleSort(USER_SORT_FIELD.ID)} className={styles.colButtons}>
                            <img id='id' className={styles.colButtonsImage} src={getArrowSrc(USER_SORT_FIELD.ID)} alt="sorting" />
                        </button>
                    </Col>

                    <Col sm="2">Felhasználónév
                        <button onClick={() => toggleSort(USER_SORT_FIELD.USERNAME)} className={styles.colButtons}>
                            <img className={styles.colButtonsImage} src={getArrowSrc(USER_SORT_FIELD.USERNAME)} alt="sorting" />
                        </button>
                    </Col>

                    <Col sm="2" className='text-center'>UUID</Col>

                    <Col sm="2" className='text-center'>TOTP Seed</Col>

                    <Col sm="2">Aktív?
                        <button onClick={() => toggleSort(USER_SORT_FIELD.ACTIVE)} className={styles.colButtons}>
                            <img className={styles.colButtonsImage} src={getArrowSrc(USER_SORT_FIELD.ACTIVE)} alt="sorting" />
                        </button>
                    </Col>

                    <Col sm="2">Qr kód</Col>
                </Row>
                
                {
                    loading
                        ? <LoadingSkeleton rows={pagination.recordsPerPage} cells={4} />
                        : showError
                            ? <ErrorMessage message={errorMessage}/>
                            : vpnUsers.map((user, index) => <User key={`vpn-user-id-${user.id}`} user={user} modifyUser={modifyUser} odd={ index % 2 === 1 } />)
                }
            </Container>

            <Container className={ styles.lowerPaginationContainer }>
                <Row className="g-0 position-relative">
                    <Col className='col-4'/>
                    <Col className='col-8 d-flex relative align-items-center flex-row-reverse'>
                        <Pagination
                            maxPages={ maxPages }
                            currentPage={ pagination.currentPage }
                            recordsPerPageProps="Felhasználó / Oldal"
                            recordsPerPage={ pagination.recordsPerPage }
                            modifyPage={page => {
                                modifyPage(page)
                            }}
                            modifyRecordsPerPage={rpp => {
                                modifyRecordsPerPage(rpp)
                            }}
                        />
                    </Col>
                </Row>
            </Container>

            <CreateUser
                showModal={showCreateUserModal}
                onHide={() => setShowCreateUserModal(false)}
                triggerFetch={() => getVpnUsers()}
            />
            
            <Notification
                position="bottom-right"
                alerts={ alerts }
                dismiss={ setAlerts }
                as={ Collapse }
            />
        </>
    )
}